import { Link, useNavigate, useParams } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { AnalyticsPage } from "@/components/analytics/AnalyticsPage";
import { Icon } from "@/components/analytics/Icon";
import { Donut, Kpi, ModeBadge, OriginBars, OriginTag } from "@/components/analytics/pieces";
import { fmt } from "@/lib/analytics/demo-data";
import { useAnalyticsData } from "@/lib/analytics/range";

export function AnalyticsAvatarDetailPage() {
  const { avatarId = "" } = useParams();
  const navigate = useNavigate();
  const { avatarById, sessions, downloadTranscript } = useAnalyticsData();
  const a = avatarById(avatarId);

  if (!a) {
    return (
      <AnalyticsPage title="Live Avatar analytics" description="Session analytics for a single live avatar.">
        <div className="page-head">
          <div>
            <h1>Avatar not found</h1>
            <p>
              This avatar isn’t in the selected range or data source.{" "}
              <Link to="/trainer/analytics/avatars">Back to live avatars</Link>
            </p>
          </div>
        </div>
      </AnalyticsPage>
    );
  }

  const list = sessions.filter((s) => s.avatar === a.id);

  return (
    <AnalyticsPage
      title={`${a.name} · Live Avatar analytics`}
      description={`${a.mode} avatar: ${a.sessions} sessions, ${a.completionRate}% completion${a.is_scorable && a.passRate !== null ? `, ${a.passRate}% pass rate` : ", playback-only review"}.`}
    >
      <button
        type="button"
        onClick={() => navigate("/trainer/analytics/avatars")}
        className="back-btn"
        aria-label="Back to avatars list"
      >
        <ChevronLeft className="h-4 w-4" />
        Back
      </button>

      <div className="adet-head">
        <div className="big-thumb">
          <Icon name={a.is_scorable ? "verified" : "record_voice_over"} />
        </div>
        <div>
          <h1>{a.name}</h1>
          <div className="meta">
            <ModeBadge mode={a.mode} />
            <span className="dot-sep" />
            {a.is_scorable
              ? a.pass_criteria != null
                ? `Scorable · pass ≥ ${a.pass_criteria}%`
                : "Scorable"
              : "Not scored"}

            <span className="dot-sep" />
            {a.uniqueLearners} unique learners
          </div>
        </div>
      </div>

      <div className="kpi-row">
        <Kpi
          icon="forum"
          label="Total sessions"
          value={fmt(a.sessions)}
          sub={`${a.uniqueLearners} unique learners`}
        />
        <Kpi
          icon="schedule"
          label="Streaming minutes"
          value={fmt(a.streamingTotal)}
          unit="min"
          sub={`avg ${a.streamingAvg} min / session`}
        />
        <Kpi
          icon="task_alt"
          label="Completion rate"
          value={a.completionRate}
          unit="%"
          sub="completed ÷ total"
        />
        <Kpi
          icon="grade"
          label="Avg score"
          value={
            a.avgScore !== null ? (
              a.avgScore
            ) : (
              <span className="dash" style={{ fontSize: "20px" }}>
                Not scored
              </span>
            )
          }
          unit={a.avgScore !== null ? "%" : undefined}
          sub={a.is_scorable ? "scorable schema" : "roleplay · playback only"}
        />
      </div>

      {a.is_scorable && a.scoreDist ? (
        <div className="grid-2">
          <div className="panel">
            <div className="panel-h">
              <Icon name="military_tech" />
              <h3>Pass / fail</h3>
              <div className="spacer" />
              {a.pass_criteria != null && (
                <span className="panel-note">threshold {a.pass_criteria}%</span>
              )}
            </div>
            <div className="donut-wrap">
              <Donut pass={a.passRate!} />
              <div className="legend">
                <div className="li">
                  <span className="dot" style={{ background: "#3A57FA" }} />
                  <div>
                    <b>{Math.round((a.sessions * a.passRate!) / 100)} passed</b>
                    <div className="sub">{a.pass_criteria != null ? `scored ≥ ${a.pass_criteria}%` : "passed"}</div>
                  </div>
                </div>
                <div className="li">
                  <span className="dot" style={{ background: "#F5B9C2" }} />
                  <div>
                    <b>{a.sessions - Math.round((a.sessions * a.passRate!) / 100)} did not pass</b>
                    <div className="sub">{a.pass_criteria != null ? `below ${a.pass_criteria}%` : "did not pass"}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="panel">
            <div className="panel-h">
              <Icon name="bar_chart" />
              <h3>Score distribution</h3>
            </div>
            <div className="hist">
              <div
                className="passline"
                style={{
                  left: `${(a.scoreDist.findIndex((d) => d.pass) / a.scoreDist.length) * 100}%`,
                }}
              >
                {a.pass_criteria != null && <span className="pl-tag">pass {a.pass_criteria}%</span>}
              </div>
              {a.scoreDist.map((d) => {
                const maxN = Math.max(...a.scoreDist!.map((x) => x.n));
                return (
                  <div className="col" key={d.r}>
                    <div
                      className={`bar ${d.pass ? "pass" : "fail"}`}
                      style={{ height: `${Math.round((d.n / maxN) * 100)}%` }}
                    />
                    <div className="xlab">{d.r}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ) : (
        <div className="panel">
          <div className="empty-scoring">
            <Icon name="videocam" />
            <b>No scoring on this avatar</b>
            <p>
              This is a {a.mode.toLowerCase()} avatar (not scorable). There is no auto-score: the
              recordings and transcripts are the analytics, reviewed by a trainer.
            </p>
          </div>
        </div>
      )}

      <div className="panel" style={{ marginTop: 22 }}>
        <div className="panel-h">
          <Icon name="hub" />
          <h3>Sessions by channel</h3>
        </div>
        <OriginBars data={a.origin} total={a.sessions} />
      </div>

      <div className="panel-h" style={{ marginTop: 4 }}>
        <Icon name="forum" />
        <h3>Sessions</h3>
      </div>

      <div className="tbl-wrap">
        <table>
          <thead>
            <tr>
              <th>Learner ID</th>
              <th>Learner</th>
              <th>Date &amp; time</th>
              <th>Origin</th>
              <th className="num">Duration</th>
              {a.is_scorable && (
                <>
                  <th className="num">Score</th>
                  <th className="num">Pass</th>
                </>
              )}
              <th className="num">Transcript</th>
            </tr>
          </thead>
          <tbody>
            {list.map((s) => (
              <tr key={s.id}>
                <td>
                  {s.learnerId ? (
                    <span className="lid">{s.learnerId}</span>
                  ) : (
                    <span className="lid none">—</span>
                  )}
                </td>
                <td className="lname">
                  {s.learnerId ? (
                    <span className="nm inline-flex items-center gap-1.5">
                      {s.learnerThumb && (
                        <img
                          src={s.learnerThumb}
                          alt=""
                          loading="lazy"
                          className="h-5 w-5 shrink-0 rounded-full object-cover"
                        />
                      )}
                      {s.learner}
                    </span>
                  ) : (
                    <span className="anon">Anonymous</span>
                  )}
                </td>
                <td className="muted-cell">{s.when}</td>
                <td>
                  <OriginTag origin={s.origin} />
                </td>
                <td className="num">{s.min} min</td>
                {a.is_scorable && (
                  <>
                    <td className="num">
                      {s.score !== null ? `${s.score}%` : <span className="pill na">—</span>}
                    </td>
                    <td className="num">
                      {s.score !== null ? (
                        s.pass ? (
                          <span className="pill pass">Yes</span>
                        ) : (
                          <span className="pill fail">No</span>
                        )
                      ) : (
                        <span className="pill na">—</span>
                      )}
                    </td>
                  </>
                )}
                <td className="num">
                  <button
                    className="dl-btn"
                    title="Download session transcript"
                    aria-label="Download session transcript"
                    onClick={() => downloadTranscript(s)}
                  >
                    <Icon name="download" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </AnalyticsPage>
  );
}